import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// Basic styling - can be replaced with a proper styling solution (CSS modules, styled-components, etc.)
const styles = {
  container: {
    position: 'relative',
    fontFamily: 'Arial, sans-serif',
  },
  tagDisplay: {
    border: '1px solid #ccc',
    paddingLeft: '5px',
    //minHeight: '34px', // Match typical input height
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '5px',
  },
  tag: {
    backgroundColor: '#e0e0e0',
    padding: '0 8px',
    margin: '2px 0',
    borderRadius: '3px',
    display: 'inline-flex',
    alignItems: 'center',
  },
  deleteButton: {
    background: 'none',
    border: 'none',
    color: '#666',
    marginLeft: '4px',
    cursor: 'pointer',
    padding: '0 2px',
    fontSize: '14px',
    fontWeight: 'bold',
    borderRadius: '2px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '16px',
    height: '16px',
    transition: 'background-color 0.2s, color 0.2s',
  },
  placeholder: {
    color: '#aaa',
    lineHeight: '24px',
    fontSize: '10px',
  },
  overlay: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    border: '1px solid #ccc',
    backgroundColor: 'white',
    zIndex: 1000,
    maxHeight: '200px',
    overflowY: 'auto',
    boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
    padding: '10px',
  },
  userItem: {
    display: 'flex',
    alignItems: 'center',
  },
  checkbox: {
    margin: '0 5px 0 0',
  },
  userInfo: {
    fontSize: '14px',
    cursor: 'pointer',
    margin: '0',
  },
  emailCount: {
    marginLeft: 'auto',
    fontSize: '12px',
    color: '#777',
  },
  noUsers: {
    padding: '5px',
    color: '#777',
  },
  controlButtons: {
    display: 'flex',
    paddingBottom: '8px',
    borderBottom: '1px solid #eee',
    marginBottom: '8px',
  },
  controlButton: {
    background: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '12px',
    cursor: 'pointer',
    color: '#333',
    transition: 'background-color 0.2s',
    minWidth: '80px',
  }
};

const CsUserFilter = ({ users, leaderboard, selectedUserList, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const [showDefaultValue, setShowDefaultValue] = useState(true);

  let selectedUsers;
  if (showDefaultValue) {
    selectedUsers = (users || []).map(u => u.value);
  } else {
    selectedUsers = selectedUserList;
  }

  useEffect(() => {
    if (selectedUserList.length > 0 && showDefaultValue) {
      setShowDefaultValue(false);
    }
  }, [selectedUserList, showDefaultValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleOverlay = () => {
    setIsOpen(!isOpen);
  };

  const handleUserSelectionChange = (userId, isChecked) => {
    let newSelectedUsers;
    if (isChecked) {
      newSelectedUsers = [...selectedUsers, userId];
    } else {
      newSelectedUsers = selectedUsers.filter(id => id !== userId);
    }
    onChange(newSelectedUsers);
  };

  const getEmailCount = (userId) => {
    const leaderEntry = leaderboard && leaderboard.find(entry => String(entry.user_id) === String(userId));
    if (leaderEntry) {
      return `(${leaderEntry.num_emails} orphaned emails)`;
    }
    return '(0 orphaned emails)';
  };

  const handleRemoveUser = (userIdToRemove) => {
    const newSelectedUsers = selectedUsers.filter(id => id !== userIdToRemove);
    onChange(newSelectedUsers);
  };

  const handleToggleAll = () => {
    if (isAllSelected) {
      // If all selected, reset to none
      onChange([]);
      setShowDefaultValue(false);
    } else {
      // If some or none selected, select all
      const allUserIds = users.map(user => user.value);
      onChange(allUserIds);
    }
  };

  const isAllSelected = users && users.length > 0 && selectedUsers.length === users.length;

  const renderSelectedUserTags = () => {
    if (!selectedUsers || selectedUsers.length === 0) {
      return <span style={styles.placeholder}>Select users...</span>;
    }
    return selectedUsers.map(userId => {
      const user = users.find(u => u.value === userId);
      const leaderEntry = leaderboard && leaderboard.find(entry => String(entry.user_id) === String(userId));
      let numEmails = 0;
      if (leaderEntry) {
        numEmails = leaderEntry.num_emails;
      }
      const userName = user ? user.name : `ID: ${userId}`;
      return (
        <span key={userId} style={styles.tag}>
          {userName} ({numEmails})
          <button
            style={styles.deleteButton}
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering the overlay toggle
              handleRemoveUser(userId);
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#ff4444';
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#666';
            }}
            title={`Remove ${userName}`}
            aria-label={`Remove ${userName}`}
          >
            ×
          </button>
        </span>
      );
    });
  };

  return (
    <div style={styles.container} ref={containerRef}>
      <div
        style={styles.tagDisplay}
        onClick={handleToggleOverlay}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleToggleOverlay();}}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title="Click to select CS users"
      >
        {renderSelectedUserTags()}
      </div>
      {isOpen && (
        <div style={styles.overlay} role="listbox">
          {/* Control button */}
          <div style={styles.controlButtons}>
            <button
              style={styles.controlButton}
              onClick={handleToggleAll}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#e8e8e8';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#f5f5f5';
              }}
              title={isAllSelected ? 'Reset all selections' : 'Select all users'}
            >
              {isAllSelected ? 'Reset All' : 'Select All'}
            </button>
          </div>
          {users && users.length > 0 ? users.map(user => {
            const userId = user.value;
            const userName = user.name || `User ${userId}`;
            const isChecked = selectedUsers.includes(userId);
            const emailCountText = getEmailCount(userId);
            const checkboxId = `user-checkbox-${userId}`;

            return (
              <div key={userId} style={styles.userItem}>
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  checked={isChecked}
                  onChange={(e) => handleUserSelectionChange(userId, e.target.checked)}
                  id={checkboxId}
                />
                <label htmlFor={checkboxId} style={styles.userInfo}>
                  {userName}
                </label>
                {emailCountText && <span style={styles.emailCount}>{emailCountText}</span>}
              </div>
            );
          }) : <div style={styles.noUsers}>No users available.</div>}
        </div>
      )}
    </div>
  );
};

CsUserFilter.propTypes = {
  users: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.any.isRequired,
    })
  ),
  leaderboard: PropTypes.arrayOf(PropTypes.object),
  selectedUsers: PropTypes.arrayOf(PropTypes.any),
  onChange: PropTypes.func.isRequired,
};

CsUserFilter.defaultProps = {
  users: [],
  leaderboard: {},
  selectedUsers: [],
};

export default CsUserFilter;